import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import FAQ from './FAQ';
import SEO from './SEO';
import { getFAQSchema, getBreadcrumbSchema, getOrganizationSchema } from '../utils/structuredData';
import './FAQPage.css';

const FAQPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Breadcrumb data for FAQ page
  const breadcrumbData = [
    { name: 'Home', url: '/' },
    { name: 'FAQ', url: '/#faq' }
  ];

  // Combined structured data for FAQ page
  const combinedStructuredData = [
    getFAQSchema(),
    getBreadcrumbSchema(breadcrumbData),
    getOrganizationSchema()
  ];

  return (
    <div className="faq-page">
      <SEO
        title="Frequently Asked Questions - PDF Tools Help Center"
        description="Get answers to common questions about our PDF converter and page remover tools. Learn about supported formats, file sizes, privacy, security, and troubleshooting tips."
        keywords="PDF FAQ, PDF converter help, PDF page remover questions, file conversion support, PDF tools help, troubleshooting PDF, PDF security questions"
        url="/#faq"
        type="article"
        canonical={`${window.location.origin}/#faq`}
        structuredData={combinedStructuredData}
      />

      <Header />

      <main className="faq-page-main">
        {/* Page Header */}
        <div className="faq-page-header">
          <div className="landing-container-full">
            <div className="faq-page-header-content">
              <button
                className="btn back-btn-faq"
                onClick={() => window.location.hash = 'home'}
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  padding: '8px 16px',
                  fontSize: '14px',
                  zIndex: 10
                }}
              >
                ← Back to Home
              </button>

              <div className="faq-page-hero">
                <div className="faq-hero-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="#3b82f6" fillOpacity="0.1" stroke="#3b82f6" strokeWidth="2"/>
                    <circle cx="24" cy="18" r="2" fill="#3b82f6"/>
                    <path d="M24,22v12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>

                <h1 className="faq-page-title">
                  Frequently Asked Questions
                </h1>

                <p className="faq-page-description">
                  Everything you need to know about our PDF converter and page remover tools.
                  Find quick answers to common questions about features, security, file formats, and troubleshooting.
                </p>

                <div className="faq-page-stats">
                  <div className="faq-stat">
                    <span className="stat-number">26+</span>
                    <span className="stat-label">Questions Answered</span>
                  </div>
                  <div className="faq-stat">
                    <span className="stat-number">5</span>
                    <span className="stat-label">Categories</span>
                  </div>
                  <div className="faq-stat">
                    <span className="stat-number">24/7</span>
                    <span className="stat-label">Available</span>
                  </div>
                </div>

                <div className="faq-page-quick-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Browse Questions
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => window.location.hash = '#feedback'}
                  >
                    Ask a Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Content */}
        <FAQ />

        {/* Additional Help Section */}
        <section className="additional-help">
          <div className="landing-container-full">
            <div className="additional-help-content">
              <h2>Need More Help?</h2>
              <p>Can't find what you're looking for? We're here to assist you!</p>

              <div className="help-options">
                <div className="help-option">
                  <div className="help-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48">
                      <defs>
                        <linearGradient id="contactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#4facfe" />
                          <stop offset="100%" stopColor="#00f2fe" />
                        </linearGradient>
                      </defs>
                      <rect width="48" height="48" rx="12" fill="url(#contactGradient)" fillOpacity="0.1"/>
                      <path d="M12 16h24v16H12V16z" stroke="url(#contactGradient)" strokeWidth="2" fill="none"/>
                      <path d="M12 16l12 8 12-8" stroke="url(#contactGradient)" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                  <h3>Contact Support</h3>
                  <p>Send us your questions and we'll get back to you quickly.</p>
                  <button
                    className="btn btn-outline"
                    onClick={() => window.location.hash = '#feedback'}
                  >
                    Contact Us
                  </button>
                </div>

                <div className="help-option">
                  <div className="help-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48">
                      <defs>
                        <linearGradient id="toolsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#fa709a" />
                          <stop offset="100%" stopColor="#fee140" />
                        </linearGradient>
                      </defs>
                      <rect width="48" height="48" rx="12" fill="url(#toolsGradient)" fillOpacity="0.1"/>
                      <rect x="12" y="12" width="12" height="16" rx="2" stroke="url(#toolsGradient)" strokeWidth="2" fill="none"/>
                      <path d="M28 12l4 4-4 4M24 20h8" stroke="url(#toolsGradient)" strokeWidth="2" strokeLinecap="round"/>
                      <rect x="32" y="16" width="4" height="8" fill="url(#toolsGradient)" fillOpacity="0.3"/>
                    </svg>
                  </div>
                  <h3>Try Our Tools</h3>
                  <p>The best way to learn is by trying. Start converting or editing PDFs now.</p>
                  <div className="tool-buttons">
                    <button
                      className="btn btn-sm"
                      onClick={() => window.location.hash = '#converter'}
                    >
                      PDF Converter
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => window.location.hash = '#page-remover'}
                    >
                      Page Remover
                    </button>
                  </div>
                </div>

                <div className="help-option">
                  <div className="help-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48">
                      <defs>
                        <linearGradient id="guideGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#a8edea" />
                          <stop offset="100%" stopColor="#fed6e3" />
                        </linearGradient>
                      </defs>
                      <rect width="48" height="48" rx="12" fill="url(#guideGradient)" fillOpacity="0.1"/>
                      <rect x="14" y="10" width="20" height="26" rx="2" stroke="url(#guideGradient)" strokeWidth="2" fill="none"/>
                      <path d="M18 18h12M18 22h12M18 26h8" stroke="url(#guideGradient)" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="20" cy="14" r="1" fill="url(#guideGradient)"/>
                    </svg>
                  </div>
                  <h3>How-to Guide</h3>
                  <p>Step-by-step instructions on using our PDF tools effectively.</p>
                  <button
                    className="btn btn-outline"
                    onClick={() => window.location.hash = '#how-it-works'}
                  >
                    View Guide
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          className="scroll-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </button>
      )}

      <Footer />
    </div>
  );
};

export default FAQPage;